/**
 * Shared utility functions for transaction form components.
 *
 * These are pure functions with no component or service dependencies.
 * Import from here in any doc-type form component (AR invoice, AP invoice,
 * sales order, purchase order, etc.) to avoid duplication.
 */

/**
 * Recursively normalizes an object for deep-equality comparison.
 *
 * - Sorts object keys alphabetically so JSON.stringify order is stable.
 * - Treats `undefined` and `''` (empty string) as equivalent to `null`
 *   (omits them from the result), preventing Angular two-way binding
 *   from writing `""` into an unset field and causing false positives
 *   in the unsaved-changes guard.
 *
 * @param obj - Any value (primitive, array, or object).
 * @returns A normalized, key-sorted representation suitable for JSON.stringify comparison.
 */
export function cleanAndSort(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return (obj === '' || obj === undefined) ? null : obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item: any) => cleanAndSort(item));
  }
  return Object.keys(obj)
    .sort()
    .reduce((result: any, key) => {
      const val = obj[key];
      if (val !== undefined && val !== '') {
        result[key] = cleanAndSort(val);
      }
      return result;
    }, {});
}

/**
 * Returns true if `current` differs from `original` using normalized deep comparison.
 *
 * Uses `cleanAndSort` on both sides so that `null`/`undefined`/`""` differences
 * and key-ordering differences do not produce false positives.
 *
 * @param current  - The live form data object (e.g. `vm.item`).
 * @param original - The baseline snapshot taken at load or after last save.
 * @returns `true` if there are unsaved changes, `false` otherwise.
 */
export function hasFormChanges(current: any, original: any): boolean {
  try {
    return JSON.stringify(cleanAndSort(current)) !== JSON.stringify(cleanAndSort(original));
  } catch {
    return false;
  }
}
